// pages/ControlPortalPage.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Monitor, Building, School, Users, Check, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const ControlPortalPage = () => {
  const navigate = useNavigate();
  const [currentPortalType, setCurrentPortalType] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const portalTypes = [
    {
      id: 'Community',
      title: 'Community Portal',
      description: 'General community-based portal for open discussions and networking',
      icon: Users,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-200',
      selectedBg: 'bg-blue-50',
      selectedBorder: 'border-blue-500'
    },
    {
      id: 'University',
      title: 'University Portal',
      description: 'Academic institution portal with alumni and student management',
      icon: Building,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200',
      selectedBg: 'bg-green-50',
      selectedBorder: 'border-green-500'
    },
    {
      id: 'School',
      title: 'School Portal',
      description: 'K-12 school portal with grade-specific features and parent access',
      icon: School,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-200',
      selectedBg: 'bg-purple-50',
      selectedBorder: 'border-purple-500'
    }
  ];

  useEffect(() => {
    fetchCurrentPortalType();
  }, []);

  const fetchCurrentPortalType = async () => {
    try {
      setLoading(true);
      const response = await axios.get(process.env.REACT_APP_API_URL + '/api/portal-control-type');
      
      if (response.data.success) {
        setCurrentPortalType(response.data.data.portalType);
      }
    } catch (error) {
      console.error('Error fetching current portal type:', error);
      showMessage('Failed to fetch current portal type', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updatePortalType = async (portalType) => {
    try {
      setUpdating(true);
      const response = await axios.put(process.env.REACT_APP_API_URL + '/api/portal-control-type', {
        portalType
      });

      if (response.data.success) {
        setCurrentPortalType(portalType);
        showMessage(response.data.message || `Portal type updated to ${portalType}`, 'success');
      }
    } catch (error) {
      console.error('Error updating portal type:', error);
      showMessage(
        error.response?.data?.message || 'Failed to update portal type', 
        'error'
      );
    } finally {
      setUpdating(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => {
      setMessage({ text: '', type: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header with Back Button */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dev-admin')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Dashboard</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Portal Control</h1>
        <p className="text-gray-600">Configure your portal type - this setting affects the entire portal</p>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
          message.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-700'
            : 'bg-red-100 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <Check className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      ) : (
        <>
          {/* Current Portal Type Display */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Current Portal Type</h2>
                <div className="flex items-center space-x-3">
                  <span className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    currentPortalType === 'Community' ? 'bg-blue-100 text-blue-800' :
                    currentPortalType === 'University' ? 'bg-green-100 text-green-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {currentPortalType} Portal
                  </span>
                  {currentPortalType && (
                    <Check className="h-5 w-5 text-green-600" />
                  )}
                </div>
              </div>
              <Monitor className="h-12 w-12 text-gray-400" />
            </div>
          </div>

          {/* Portal Type Selection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Select Portal Type</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {portalTypes.map((portal) => {
                const IconComponent = portal.icon;
                const isSelected = currentPortalType === portal.id;
                const isUpdating = updating;

                return (
                  <div
                    key={portal.id}
                    onClick={() => !isUpdating && updatePortalType(portal.id)}
                    className={`relative border-2 rounded-lg p-6 transition-all duration-200 ${
                      isSelected 
                        ? `${portal.selectedBg} ${portal.selectedBorder}` 
                        : `bg-white ${portal.borderColor} hover:${portal.selectedBg}`
                    } ${isUpdating ? 'cursor-wait opacity-75' : 'cursor-pointer hover:shadow-md'}`}
                  >
                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className="absolute top-4 right-4">
                        <div className="bg-green-500 rounded-full p-1">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      </div>
                    )}

                    {/* Portal Icon */}
                    <div className={`p-3 ${portal.bgColor} rounded-lg mb-4 w-fit`}>
                      <IconComponent className={`h-8 w-8 ${portal.iconColor}`} />
                    </div>

                    {/* Portal Content */}
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-gray-900">{portal.title}</h3>
                      <p className="text-gray-600 text-sm">{portal.description}</p>
                    </div>

                    {/* Action Text */}
                    <div className="mt-4">
                      {isSelected ? (
                        <div className="text-green-600 text-sm font-medium flex items-center">
                          <Check className="h-4 w-4 mr-1" />
                          Currently Active
                        </div>
                      ) : (
                        <div className={`${portal.iconColor.replace('text-', 'text-')} text-sm font-medium`}>
                          Switch to {portal.title}
                        </div>
                      )}
                    </div>

                    {/* Loading Overlay */}
                    {isUpdating && (
                      <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Help Text */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-700">
                  <p className="font-medium mb-1">Important:</p>
                  <p>Changing the portal type will affect the entire portal experience for all users. This includes available features, user interface elements, and access permissions.</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ControlPortalPage;
